import { FormHelperText, InputLabel, Stack, Typography } from '@mui/material';
import React from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import FormTextField from '../../../../shared/FormTextField';
import { ExperienceType } from '../../../../enums/experience';
import { FormValues, Guide } from '../../../../types/experience';
import FormUpload from '../../../../shared/FormUpload';
import UserRole from '../../../../../shared/AuthRoles';
import toast from '../../../../shared/Toast';
import {
    useAddGuideThumbnailMutation,
    useAddPresignedGuideUrlMutation,
    useUploadGuideMutation,
} from '../../../../../app/Slices/SevenDayApiSlice';
import { AccessTime } from '@mui/icons-material';
import GuideList from './GuideList';
import { generateThumbnailFromVideo } from '../../../../../shared/utils';
import { useFormState } from '../../Form/useFormState';

const GuideUpload = ({ guides }: { guides: Guide[] }) => {
    const loggedInUser = UserRole();
    const { control } = useFormContext<FormValues>();
    const { isSaveDisabled, setIsSaveDisabled } = useFormState();

    const [getPresignedUrl] = useAddPresignedGuideUrlMutation();
    const [uploadGuide] = useUploadGuideMutation();
    const [addGuideThumbnail] = useAddGuideThumbnailMutation();

    const handleVideos = async (files: File[]) => {
        if (!files.length) return;

        setIsSaveDisabled(true);
        const ownerId = loggedInUser.company;
        const uploadedVideos: Guide[] = [];

        try {
            for (const file of files) {
                // Step 1: Get presigned URL
                const presign = await getPresignedUrl({
                    ownerId,
                    fileName: file.name,
                    contentType: file.type,
                }).unwrap();

                // Step 2: Upload to S3
                const uploadRes = await fetch(presign.uploadUrl, {
                    method: 'PUT',
                    headers: { 'Content-Type': file.type },
                    body: file,
                });

                if (!uploadRes.ok) {
                    toast.error('S3 upload failed.');
                    continue;
                }

                let thumbnail;
                // Step 3: Upload thumbnail for videos
                if (file.type.startsWith('video')) {
                    const thumbnailBlob = await generateThumbnailFromVideo(
                        presign.fileUrl,
                    );
                    const formData = new FormData();
                    formData.append(
                        'files',
                        thumbnailBlob,
                        `${presign.id}_thumb.jpg`,
                    );

                    thumbnail = await addGuideThumbnail([
                        ownerId,
                        formData,
                    ]).unwrap();
                }

                // Step 4: Confirm video upload
                await uploadGuide({
                    ownerId,
                    id: presign.id,
                    fileUrl: presign.fileUrl,
                    name: file.name,
                    mimeType: file.type,
                    thumbnail: thumbnail?.src,
                }).unwrap();

                // Step 5: Add to uploaded videos array
                uploadedVideos.push({
                    id: presign.id,
                    src: presign.fileUrl,
                    name: file.name,
                    mimeType: file.type,
                    thumbnail: thumbnail?.src,
                });
            }

            return uploadedVideos;
        } catch (err) {
            console.error(err);
            toast.error('Something went wrong.');
            return undefined;
        } finally {
            setIsSaveDisabled(false);
        }
    };

    return (
        <FormUpload
            name='guides'
            control={control}
            validation={{
                acceptedTypes: {
                    'video/*': [
                        '.mp4',
                        '.mov',
                        '.avi',
                        '.mkv',
                        '.webm',
                        '.m4v',
                    ],
                    'image/*': ['.png', '.jpg', '.jpeg', '.webp'],
                    'application/pdf': ['.pdf'],
                },
                maxFileSize: 100 * 1024 * 1024, // 100MB
                maxFiles: 10,
            }}
            showFiles={false}
            disabled={guides.length >= 10 || isSaveDisabled}
            onUpload={handleVideos}
        />
    );
};

const CheckinGuide = () => {
    const { control } = useFormContext<FormValues>();

    const { experience } = useFormState();
    const type = experience.type;

    // const checkinDetails = useWatch({
    //     control,
    //     name: 'checkinDetails',
    //     defaultValue: '',
    // });

    // const wifiDetails = useWatch({
    //     control,
    //     name: 'details.wifiDetails',
    //     defaultValue: '',
    // });

    const guides = useWatch({ name: 'guides', defaultValue: [] });
    const { remove } = useFieldArray({ name: 'guides' });

    const showWifiDetails =
        type === ExperienceType.Boat ||
        type === ExperienceType.Commercial ||
        type === ExperienceType.Property;

    return (
        <fieldset>
            <Stack spacing={2}>
                <div>
                    <Typography variant='h2'>
                        Guest Check-in Guide (optional)
                    </Typography>
                    <Typography sx={{ color: 'text.secondary' }} gutterBottom>
                        Provide clear instructions and helpful resources for
                        your guests
                    </Typography>
                </div>
                <div>
                    <InputLabel htmlFor='checkinDetails__input'>
                        Access Details (optional)
                    </InputLabel>
                    <FormTextField
                        id='checkinDetails__input'
                        name='checkinDetails'
                        control={control}
                        placeholder='Add any important details for getting access i.e Door code: 0000'
                        inputProps={{ maxLength: 500 }}
                        multiline
                        rows={4}
                    />
                    <FormHelperText>
                        <AccessTime sx={{ fontSize: '14px' }} /> This
                        information will be displayed 24 hours before check-in
                    </FormHelperText>
                </div>
                {showWifiDetails && (
                    <div>
                        <InputLabel htmlFor='wifiDetails__input'>
                            WiFi Details (optional)
                        </InputLabel>
                        <FormTextField
                            id='wifiDetails__input'
                            name='details.wifiDetails'
                            control={control}
                            placeholder='Network name: GuestHouse_5G. Password: welcome123'
                            inputProps={{ maxLength: 500 }}
                            multiline
                            rows={4}
                        />
                        <FormHelperText>
                            <AccessTime sx={{ fontSize: '14px' }} /> This
                            information will be displayed 24 hours before
                            check-in
                        </FormHelperText>
                    </div>
                )}
                {experience.type === ExperienceType.Vehicle && (
                    <div>
                        <InputLabel htmlFor='hiring-rules__input'>
                            Additional Hiring Rules (optional)
                        </InputLabel>
                        <FormTextField
                            id='hiring-rules__input'
                            name='details.rules'
                            control={control}
                            placeholder={
                                'You can add any additional hiring rules. (max 500 characters)'
                            }
                            inputProps={{ maxLength: 500 }}
                            multiline
                            rows={4}
                        />
                    </div>
                )}
                <div>
                    <Typography variant='h3'>Upload Guest Resources</Typography>
                    <Typography
                        variant='body2'
                        sx={{ color: 'text.secondary' }}
                    >
                        Add videos, images and documents to help your guests.
                    </Typography>
                </div>
                <div>
                    <GuideUpload guides={guides} />
                    <GuideList guides={guides} handleRemove={remove} />
                </div>
            </Stack>
        </fieldset>
    );
};

export default CheckinGuide;
